export interface GDTopicData {
  slug: string;
  title: string;
  category: string;
  overview: string;
  argumentsFor: string[];
  argumentsAgainst: string[];
  discussionPoints: string[];
  openingStatements: string[];
  relatedSlugs: string[];
}

export const gdTopicsData: GDTopicData[] = [
  {
    slug: "artificial-intelligence-impact-on-jobs",
    title: "Impact of Artificial Intelligence on Jobs",
    category: "Technology",
    overview: "Artificial intelligence is rapidly transforming industries worldwide. While AI creates new job categories in data science, machine learning, and AI ethics, it also automates routine tasks traditionally performed by humans. This topic explores whether AI is a net job creator or destroyer, and how the workforce should adapt.",
    argumentsFor: [
      "AI creates entirely new job categories — data scientists, ML engineers, AI ethicists, prompt engineers",
      "Automation of routine tasks allows humans to focus on creative and strategic work",
      "AI increases productivity and economic output, leading to new business opportunities",
      "Historical precedent: every industrial revolution created more jobs than it destroyed",
      "AI tools augment human capabilities rather than replacing humans entirely",
    ],
    argumentsAgainst: [
      "Millions of routine jobs in manufacturing, data entry, and customer service face elimination",
      "The pace of AI advancement outstrips workforce retraining capacity",
      "New AI jobs require advanced education, leaving unskilled workers behind",
      "Wealth concentration in AI companies widens economic inequality",
      "Developing countries with large low-skill labor forces are disproportionately affected",
    ],
    discussionPoints: [
      "Government's role in workforce retraining and education reform",
      "Universal Basic Income as a response to AI-driven unemployment",
      "Industry-specific impact analysis: healthcare, finance, manufacturing, education",
      "Ethical implications of replacing human judgment with AI decisions",
      "The timeline debate: gradual transition vs. sudden disruption",
    ],
    openingStatements: [
      "According to the World Economic Forum, AI will displace 85 million jobs by 2025 but create 97 million new ones — making this a question of transition, not elimination.",
      "The real question isn't whether AI will take jobs, but whether we're preparing our workforce fast enough for the jobs AI will create.",
      "When ATMs were introduced, people predicted the end of bank tellers. Instead, the number of bank branches increased. AI may follow a similar pattern.",
    ],
    relatedSlugs: ["remote-work-future-of-employment", "ethical-ai-development", "digital-india-success-or-failure"],
  },
  {
    slug: "social-media-advantages-and-disadvantages",
    title: "Social Media: Advantages and Disadvantages",
    category: "Social Issues",
    overview: "Social media has fundamentally changed how people communicate, consume information, and form opinions. With over 4.9 billion social media users worldwide, platforms like Instagram, Twitter, and YouTube have become integral to modern life. This topic examines whether social media's benefits outweigh its risks.",
    argumentsFor: [
      "Democratizes information access and gives voice to marginalized communities",
      "Enables global connectivity and cross-cultural understanding",
      "Powerful tool for businesses, especially small businesses and startups",
      "Facilitates social movements and political awareness",
      "Educational content on platforms like YouTube reaches millions for free",
    ],
    argumentsAgainst: [
      "Mental health crisis among youth linked to social media usage",
      "Spread of misinformation and fake news at unprecedented scale",
      "Privacy concerns and data exploitation by tech companies",
      "Cyberbullying and online harassment are rampant",
      "Addictive design patterns exploit human psychology for engagement",
    ],
    discussionPoints: [
      "Should social media platforms be regulated like traditional media?",
      "The role of algorithms in creating echo chambers and polarization",
      "Age restrictions and parental controls effectiveness",
      "Impact on democracy and elections",
      "Social media's role in mental health awareness vs. causing mental health issues",
    ],
    openingStatements: [
      "With the average person spending 2.5 hours daily on social media, we're looking at a technology that consumes nearly 10% of our waking lives — making this discussion highly relevant.",
      "Social media gave us the Arab Spring and also gave us Cambridge Analytica. The same tool that democratizes information can also weaponize it.",
    ],
    relatedSlugs: ["data-privacy-concerns", "mental-health-awareness", "social-media-regulation"],
  },
  {
    slug: "online-education-vs-offline-education",
    title: "Online Education vs Offline Education",
    category: "Education",
    overview: "The COVID-19 pandemic forced a global experiment in online education, and the debate continues about which model is more effective. While online education offers flexibility and accessibility, offline education provides hands-on experience and social development. This topic explores the ideal balance.",
    argumentsFor: [
      "Online education removes geographical barriers — anyone can access world-class courses",
      "Self-paced learning accommodates different learning speeds and styles",
      "Significantly lower cost than traditional campus education",
      "Technology enables interactive, multimedia-rich learning experiences",
      "Working professionals can upskill without career breaks",
    ],
    argumentsAgainst: [
      "Lack of social interaction hinders personality development and soft skills",
      "Digital divide excludes students without internet access or devices",
      "Practical subjects (medicine, engineering labs) require physical presence",
      "Online learning leads to higher dropout rates due to lack of accountability",
      "Screen fatigue affects attention and retention negatively",
    ],
    discussionPoints: [
      "Hybrid models: combining the best of both worlds",
      "Assessment integrity and cheating prevention in online exams",
      "Impact on rural education in India",
      "The role of EdTech companies vs. traditional universities",
      "Mental health impact of isolation in online-only learning",
    ],
    openingStatements: [
      "India's EdTech market is projected to reach $30 billion by 2030, but the question remains — are we replacing education with content consumption?",
      "The pandemic proved that education can happen online. But it also proved that education is about more than just transferring information.",
    ],
    relatedSlugs: ["digital-india-success-or-failure", "mental-health-awareness", "5g-and-its-impact"],
  },
  {
    slug: "startup-culture-in-india",
    title: "Startup Culture in India",
    category: "Business & Economy",
    overview: "India is the third-largest startup ecosystem globally with over 100 unicorns. The startup culture has transformed the employment landscape, innovation pipeline, and economic aspirations of young Indians. But concerns about sustainability, work-life balance, and the recent funding winter raise questions.",
    argumentsFor: [
      "Creates jobs and economic value — Indian startups employ millions",
      "Solves India-specific problems through innovation (UPI, affordable healthcare, AgriTech)",
      "Attracts foreign investment and builds India's global economic standing",
      "Encourages entrepreneurial mindset over traditional job-seeking",
      "Success stories inspire a new generation of problem-solvers",
    ],
    argumentsAgainst: [
      "Toxic work culture — 80-hour work weeks and burnout are common",
      "Most startups fail — 90% don't survive beyond 5 years",
      "Valuation-driven growth over profitability creates unsustainable businesses",
      "Layoffs during funding winters affect thousands of employees",
      "Founders from privileged backgrounds dominate, limiting diversity",
    ],
    discussionPoints: [
      "Government initiatives like Startup India — impact vs. intent",
      "The role of VCs in shaping startup culture for better or worse",
      "Tier-2 and Tier-3 city startups vs. metro-centric ecosystem",
      "Work-life balance in the startup ecosystem",
      "Should profitability or growth be the priority for early-stage startups?",
    ],
    openingStatements: [
      "India went from 0 unicorns in 2011 to over 100 in 2023. But the real question is — are we building sustainable businesses or just inflated valuations?",
      "The Indian startup ecosystem has created more millionaires in the last decade than any other sector. But at what cost to employee well-being?",
    ],
    relatedSlugs: ["gig-economy-and-worker-rights", "make-in-india-vs-import-dependency", "cryptocurrency-boon-or-bane"],
  },
  {
    slug: "climate-change-and-indias-role",
    title: "Climate Change and India's Role",
    category: "Current Affairs",
    overview: "India is the third-largest greenhouse gas emitter but also one of the most vulnerable countries to climate change. Balancing economic development with environmental sustainability is India's central challenge. This topic explores India's responsibilities, actions, and the path forward.",
    argumentsFor: [
      "India has committed to net-zero by 2070 and is scaling renewable energy rapidly",
      "India's per capita emissions are far below developed nations — historical responsibility matters",
      "Solar energy revolution: India is among the world's largest solar power producers",
      "India leads the International Solar Alliance and climate diplomacy efforts",
      "Traditional Indian practices (sustainability, frugality) align with climate goals",
    ],
    argumentsAgainst: [
      "India's total emissions are rising despite per capita being low",
      "Coal still accounts for 70%+ of India's electricity generation",
      "Rapid urbanization and industrialization are increasing the carbon footprint",
      "Enforcement of environmental regulations is weak",
      "Climate change already affecting Indian agriculture, monsoons, and water resources",
    ],
    discussionPoints: [
      "Development vs. environment: can India have both?",
      "Climate finance: should developed nations pay for developing nations' transition?",
      "Impact on Indian agriculture and food security",
      "Electric vehicle adoption and green mobility in India",
      "The role of individual action vs. systemic change",
    ],
    openingStatements: [
      "India houses 17% of the world's population but contributes only 7% of global emissions. Yet, we're among the most affected by climate change. This asymmetry defines our unique challenge.",
      "India added more renewable energy capacity in 2023 than any country except China. The question is — is it enough, and is it fast enough?",
    ],
    relatedSlugs: ["electric-vehicles-adoption", "digital-india-success-or-failure", "social-media-regulation"],
  },
  {
    slug: "cryptocurrency-boon-or-bane",
    title: "Cryptocurrency: Boon or Bane?",
    category: "Business & Economy",
    overview: "Cryptocurrencies like Bitcoin and Ethereum have grown from niche technology experiments to a trillion-dollar asset class. While proponents see decentralized finance as the future, critics point to volatility, scams, and energy consumption. India's regulatory stance remains uncertain.",
    argumentsFor: [
      "Decentralization removes dependency on traditional banking systems",
      "Financial inclusion for unbanked populations globally",
      "Blockchain technology has applications beyond currency — supply chain, healthcare, governance",
      "Potential for high returns has attracted millions of young Indian investors",
      "Smart contracts enable trustless, automated transactions",
    ],
    argumentsAgainst: [
      "Extreme price volatility makes it unsuitable as a stable currency",
      "Used for money laundering, ransomware, and illegal transactions",
      "Bitcoin mining consumes more electricity than some countries",
      "Lack of regulation means no consumer protection",
      "India's 30% crypto tax and 1% TDS have dampened legitimate trading",
    ],
    discussionPoints: [
      "Should India create its own Central Bank Digital Currency (CBDC)?",
      "Regulation vs. ban: what's the right approach for India?",
      "Crypto education and investor protection",
      "The environmental cost of proof-of-work vs. proof-of-stake",
      "Impact of global crypto regulations on India",
    ],
    openingStatements: [
      "India has over 25 million crypto investors despite a 30% tax on gains. The demand clearly exists — the question is whether we regulate it wisely or push it underground.",
      "El Salvador adopted Bitcoin as legal tender and it failed. Meanwhile, Ethereum's shift to proof-of-stake reduced energy use by 99%. The crypto space is evolving faster than regulators can keep up.",
    ],
    relatedSlugs: ["startup-culture-in-india", "digital-india-success-or-failure", "data-privacy-concerns"],
  },
  {
    slug: "remote-work-future-of-employment",
    title: "Remote Work: Future of Employment?",
    category: "Technology",
    overview: "Remote work, once a perk for a few, became mainstream during the pandemic. Now, as companies oscillate between remote, hybrid, and return-to-office policies, the debate about the future of work intensifies. This topic explores productivity, culture, and the long-term viability of remote work.",
    argumentsFor: [
      "Increases productivity for focused, independent work",
      "Reduces commute time, office costs, and carbon footprint",
      "Enables companies to hire talent globally, not just locally",
      "Better work-life balance leads to higher employee satisfaction",
      "Inclusive for people with disabilities and caregiving responsibilities",
    ],
    argumentsAgainst: [
      "Collaboration and innovation suffer without in-person interaction",
      "Blurred boundaries between work and personal life lead to burnout",
      "Junior employees miss mentorship and on-the-job learning",
      "Company culture is harder to build and maintain remotely",
      "Not feasible for all industries — manufacturing, healthcare, services",
    ],
    discussionPoints: [
      "Hybrid models: the best compromise or worst of both worlds?",
      "Impact on commercial real estate and urban planning",
      "Remote work policies and their effect on gender equality",
      "Tools and technology enabling effective remote collaboration",
      "The role of trust vs. surveillance in remote work management",
    ],
    openingStatements: [
      "A Stanford study found that remote workers are 13% more productive, yet most Fortune 500 companies are mandating return-to-office. There's clearly a disconnect worth discussing.",
      "Remote work didn't just change where we work — it changed how we think about work itself. The question is whether we embrace this shift or revert to old patterns.",
    ],
    relatedSlugs: ["artificial-intelligence-impact-on-jobs", "gig-economy-and-worker-rights", "mental-health-awareness"],
  },
  {
    slug: "mental-health-awareness",
    title: "Mental Health Awareness in India",
    category: "Social Issues",
    overview: "India faces a mental health crisis with over 150 million people needing mental health support but only 9,000 psychiatrists available. Stigma, lack of awareness, and inadequate infrastructure make mental health one of the most critical yet neglected public health issues.",
    argumentsFor: [
      "Growing awareness through social media and celebrity advocacy is reducing stigma",
      "Corporate mental health programs are becoming standard in major companies",
      "Government initiatives like NIMHANS tele-counseling are increasing access",
      "Young generation is more open about seeking therapy and counseling",
      "Digital mental health platforms are bridging the therapist shortage",
    ],
    argumentsAgainst: [
      "India spends less than 1% of its health budget on mental health",
      "Severe stigma persists, especially in rural areas and traditional families",
      "The psychiatrist-to-population ratio is 0.3 per 100,000 — far below WHO recommendation",
      "Mental health education is absent from most school curricula",
      "Insurance coverage for mental health treatment is minimal",
    ],
    discussionPoints: [
      "Role of workplaces in addressing employee mental health",
      "Social media's dual role: awareness tool vs. mental health risk",
      "Mental health education in schools and colleges",
      "Cultural barriers to seeking help in Indian society",
      "Affordable and accessible mental health solutions for Tier-2/3 cities",
    ],
    openingStatements: [
      "India has a treatment gap of 83% for mental health conditions — meaning 83 out of 100 people who need help don't receive it. This is both a health crisis and a human rights issue.",
      "The WHO estimates that depression and anxiety cost the global economy $1 trillion annually in lost productivity. Yet, we still treat mental health as a luxury, not a necessity.",
    ],
    relatedSlugs: ["social-media-advantages-and-disadvantages", "online-education-vs-offline-education", "gender-equality-in-workplaces"],
  },
  {
    slug: "electric-vehicles-adoption",
    title: "Electric Vehicles Adoption in India",
    category: "Technology",
    overview: "India aims to have 30% of all vehicles electric by 2030. With government subsidies, growing charging infrastructure, and falling battery costs, EV adoption is accelerating. But challenges around range anxiety, cost, and grid capacity remain.",
    argumentsFor: [
      "Reduces air pollution — transport contributes 13% of India's CO2 emissions",
      "Lower running costs: electricity is cheaper than petrol/diesel",
      "Government FAME II subsidies make EVs more affordable",
      "India can reduce oil import dependency, saving forex reserves",
      "Growing domestic EV manufacturing creates new jobs and industries",
    ],
    argumentsAgainst: [
      "High upfront cost compared to ICE vehicles",
      "Charging infrastructure is inadequate outside major cities",
      "Battery manufacturing relies on imported lithium and cobalt",
      "Electricity still largely generated from coal — EVs shift emissions, not eliminate them",
      "Battery disposal and recycling create new environmental challenges",
    ],
    discussionPoints: [
      "Should India focus on two-wheeler EVs (which dominate Indian roads) over cars?",
      "Role of public transport electrification vs. private EV adoption",
      "Battery swapping vs. fast charging infrastructure",
      "The hydrogen fuel cell alternative",
      "Impact on the existing auto industry and petroleum sector employment",
    ],
    openingStatements: [
      "India's EV sales grew 45% in 2023, led by two-wheelers. But we still have less than 12,000 public charging stations for a country of 1.4 billion. Infrastructure is the bottleneck.",
      "Norway has shown that with the right policies, EV adoption can reach 80% of new car sales. The question for India is — what's our version of the Norwegian model?",
    ],
    relatedSlugs: ["climate-change-and-indias-role", "startup-culture-in-india", "digital-india-success-or-failure"],
  },
  {
    slug: "gender-equality-in-workplaces",
    title: "Gender Equality in Indian Workplaces",
    category: "Social Issues",
    overview: "Despite progress, India ranks 127th out of 146 countries in the Global Gender Gap Report. Women's workforce participation has declined from 32% to 24% over the last decade. This topic explores barriers to workplace gender equality and solutions.",
    argumentsFor: [
      "Companies with diverse leadership perform 25% better financially (McKinsey)",
      "POSH Act and maternity benefit amendments have strengthened legal protections",
      "Growing number of women in tech, finance, and leadership roles",
      "Remote work has enabled more women to balance career and family",
      "Women-focused entrepreneurship programs are creating new opportunities",
    ],
    argumentsAgainst: [
      "Gender pay gap persists — women earn 19% less than men for similar roles",
      "Glass ceiling remains thick in corporate leadership and boardrooms",
      "Safety concerns limit women's participation in night shifts and travel-heavy roles",
      "Cultural expectations around caregiving disproportionately fall on women",
      "Sexual harassment and hostile work environments remain underreported",
    ],
    discussionPoints: [
      "Mandatory gender diversity targets: helpful or counterproductive?",
      "Role of men as allies in workplace gender equality",
      "Paternity leave and shared caregiving responsibilities",
      "Sector-specific challenges: tech, manufacturing, services",
      "Mentorship and sponsorship programs for women leaders",
    ],
    openingStatements: [
      "India loses $6 trillion in GDP annually due to women's exclusion from the workforce, according to McKinsey. Gender equality isn't just a social issue — it's an economic imperative.",
      "Only 5% of CEOs in India's top 500 companies are women. Despite decades of policy progress, the leadership pipeline remains broken.",
    ],
    relatedSlugs: ["mental-health-awareness", "remote-work-future-of-employment", "education-system-reform"],
  },
  {
    slug: "upi-and-digital-payments-revolution",
    title: "UPI and India's Digital Payments Revolution",
    category: "Current Affairs",
    overview: "India's Unified Payments Interface (UPI) processed over 10 billion transactions in a single month in 2023, making it the world's most successful real-time payment system. This topic explores UPI's impact, challenges, and future.",
    argumentsFor: [
      "Financial inclusion — UPI reaches rural areas where banks don't have branches",
      "Zero transaction cost for users makes digital payments accessible to all",
      "Reduced cash dependency decreases corruption and black money circulation",
      "International adoption (Singapore, UAE, France) proves global relevance",
      "Enabled the rise of small businesses and street vendors going cashless",
    ],
    argumentsAgainst: [
      "Digital divide: many rural and elderly users still struggle with smartphones",
      "Payment fraud and phishing scams are increasing with adoption",
      "Revenue model for banks and fintech companies remains unsustainable at zero MDR",
      "Over-dependence on a single system creates systemic risk",
      "Privacy concerns about transaction data being accessible to the government",
    ],
    discussionPoints: [
      "Should UPI introduce transaction fees to make it sustainable?",
      "UPI's role in India's goal of becoming a $5 trillion economy",
      "Competition between UPI apps (PhonePe, GPay, Paytm) and its implications",
      "Cross-border UPI payments: opportunities and challenges",
      "Impact on traditional banking and financial services",
    ],
    openingStatements: [
      "When India's UPI processed more real-time digital transactions than all other countries combined, it proved that innovation doesn't always come from Silicon Valley.",
      "UPI turned a country where 90% of transactions were cash-based into one where a street vendor can accept digital payments. That's not just a fintech story — it's a social revolution.",
    ],
    relatedSlugs: ["digital-india-success-or-failure", "cryptocurrency-boon-or-bane", "data-privacy-concerns"],
  },
  {
    slug: "data-privacy-concerns",
    title: "Data Privacy in the Digital Age",
    category: "Technology",
    overview: "With India's Digital Personal Data Protection Act 2023 and growing global concern about surveillance capitalism, data privacy has become a fundamental rights issue. This topic explores the tension between convenience, innovation, and individual privacy.",
    argumentsFor: [
      "India's DPDP Act 2023 establishes consent-based data collection framework",
      "Growing awareness among users about data rights and digital literacy",
      "GDPR-like regulations globally are raising the bar for data protection",
      "End-to-end encryption and privacy-focused tech are becoming mainstream",
      "Data privacy is recognized as a fundamental right by India's Supreme Court",
    ],
    argumentsAgainst: [
      "Government surveillance exemptions in India's privacy law raise concerns",
      "Most users accept terms without reading them — consent is often meaningless",
      "Big tech companies have disproportionate access to personal data",
      "Data breaches are frequent — Aadhaar data leaks have affected millions",
      "Innovation in AI and personalization requires large datasets, creating tension",
    ],
    discussionPoints: [
      "Aadhaar and the balance between identity verification and privacy",
      "Right to be forgotten: practicality and implementation",
      "Children's data protection in the age of EdTech and social media",
      "The role of encryption in privacy vs. law enforcement access",
      "Can we have personalized services without sacrificing privacy?",
    ],
    openingStatements: [
      "In 2017, India's Supreme Court declared privacy a fundamental right. In 2023, we got our first data protection law. The question now is — does the law go far enough?",
      "We willingly share our location, contacts, and browsing history with apps for free services. As the saying goes: if the product is free, you are the product.",
    ],
    relatedSlugs: ["social-media-advantages-and-disadvantages", "artificial-intelligence-impact-on-jobs", "ethical-ai-development"],
  },
  {
    slug: "gig-economy-and-worker-rights",
    title: "Gig Economy and Worker Rights",
    category: "Business & Economy",
    overview: "India's gig workforce is estimated at 7.7 million and growing. Platforms like Swiggy, Zomato, Uber, and Urban Company have created flexible employment but without traditional worker protections. This topic examines the rights and future of gig workers.",
    argumentsFor: [
      "Flexibility: gig workers choose when, where, and how much to work",
      "Low entry barriers create employment for millions without formal education",
      "Platforms enable earning opportunities in Tier-2 and Tier-3 cities",
      "Gig work can supplement traditional employment income",
      "NITI Aayog recommends social security for gig workers, showing policy awareness",
    ],
    argumentsAgainst: [
      "No job security, health insurance, or retirement benefits",
      "Algorithms control earnings and ratings — workers have little bargaining power",
      "Income is unpredictable and often below minimum wage after expenses",
      "Classification as 'partners' instead of 'employees' avoids labor law compliance",
      "Physical risks (road accidents for delivery workers) without adequate insurance",
    ],
    discussionPoints: [
      "Should gig workers be classified as employees?",
      "Role of unions and collective bargaining in the gig economy",
      "Platform accountability for worker safety and fair pay",
      "Government regulations: India's Code on Social Security 2020",
      "Impact of gig economy on traditional employment sectors",
    ],
    openingStatements: [
      "India will have 23.5 million gig workers by 2029, according to NITI Aayog. That's larger than the population of many countries. We can't afford to leave this workforce without basic protections.",
      "When a Swiggy delivery partner has an accident, they're not covered by the company's insurance because they're classified as 'partners,' not 'employees.' This classification loophole defines the gig economy debate.",
    ],
    relatedSlugs: ["startup-culture-in-india", "remote-work-future-of-employment", "gender-equality-in-workplaces"],
  },
  {
    slug: "reservation-policy-in-india",
    title: "Reservation Policy in India",
    category: "Social Issues",
    overview: "India's reservation system, designed to address historical caste-based discrimination, reserves seats in education and government jobs for SC, ST, OBC, and EWS categories. While it has uplifted millions, debates about its effectiveness, expansion, and future continue.",
    argumentsFor: [
      "Constitutional mandate to correct centuries of systemic discrimination",
      "Has enabled first-generation learners from marginalized communities to access education",
      "Representation in government institutions has improved significantly",
      "Mandal Commission and subsequent policies addressed backward class inclusion",
      "EWS reservation (2019) extends benefit to economically weak upper castes",
    ],
    argumentsAgainst: [
      "Creamy layer within reserved categories captures most benefits",
      "Merit-based opportunities may be reduced for general category candidates",
      "Reservation hasn't adequately reached the most marginalized within categories",
      "Political parties use reservation promises as vote banks",
      "Lack of periodic review means outdated beneficiary lists",
    ],
    discussionPoints: [
      "Should reservation be based on economic status rather than caste?",
      "Reservation in the private sector: feasibility and implications",
      "Time limit on reservation: constitutional perspective",
      "Intersection of caste, gender, and economic status in reservation policy",
      "International approaches to affirmative action (US, South Africa, Malaysia)",
    ],
    openingStatements: [
      "Dr. Ambedkar envisioned reservation as a temporary measure. 75 years later, the debate isn't about whether it should exist, but how it should evolve to serve its original purpose.",
      "India's reservation policy has created the world's largest affirmative action program. But the question remains — are we lifting those at the bottom, or has the system been captured by those already above?",
    ],
    relatedSlugs: ["gender-equality-in-workplaces", "education-system-reform", "mental-health-awareness"],
  },
  {
    slug: "ethical-ai-development",
    title: "Ethical AI Development",
    category: "Technology",
    overview: "As AI systems make decisions about hiring, lending, healthcare, and criminal justice, questions about bias, transparency, and accountability become critical. This topic explores the ethical frameworks needed for responsible AI development.",
    argumentsFor: [
      "AI ethics frameworks (EU AI Act, IEEE standards) are being established globally",
      "Explainable AI (XAI) makes AI decisions more transparent and auditable",
      "Diverse development teams can reduce bias in AI systems",
      "Ethical AI can actually improve fairness by removing human prejudices from decisions",
      "Companies investing in responsible AI see better long-term trust and adoption",
    ],
    argumentsAgainst: [
      "AI systems trained on biased data perpetuate and amplify existing discrimination",
      "Black-box models make it impossible to understand why certain decisions were made",
      "Race to deploy AI prioritizes speed over safety and ethics",
      "Lack of global consensus on AI ethics standards creates regulatory gaps",
      "Deepfakes and AI-generated misinformation are outpacing detection capabilities",
    ],
    discussionPoints: [
      "Who is responsible when an AI system causes harm — the developer, deployer, or user?",
      "Balancing innovation speed with ethical safeguards",
      "AI in criminal justice: risk scoring and predictive policing",
      "The role of government regulation vs. industry self-regulation",
      "AI rights and the question of artificial consciousness",
    ],
    openingStatements: [
      "Amazon scrapped its AI hiring tool when it discovered it was biased against women. If the world's most advanced tech company can't build unbiased AI, this is a systemic problem.",
      "The EU AI Act categorizes AI applications by risk level — from minimal to unacceptable. India needs a similar framework before AI becomes too deeply embedded in our systems to regulate.",
    ],
    relatedSlugs: ["artificial-intelligence-impact-on-jobs", "data-privacy-concerns", "social-media-advantages-and-disadvantages"],
  },
  {
    slug: "digital-india-success-or-failure",
    title: "Digital India: Success or Failure?",
    category: "Current Affairs",
    overview: "Launched in 2015, Digital India aimed to transform India into a digitally empowered society. From Aadhaar and UPI to DigiLocker and CoWIN, the initiative has had notable successes. But the digital divide, privacy concerns, and implementation gaps remain.",
    argumentsFor: [
      "UPI is the world's most successful real-time digital payment system",
      "Aadhaar has enabled efficient delivery of government benefits (DBT)",
      "India Stack (Aadhaar + UPI + DigiLocker) is a model for other developing nations",
      "CoWIN enabled the world's largest vaccination drive during COVID-19",
      "Over 800 million internet users — second largest online population globally",
    ],
    argumentsAgainst: [
      "Only 31% of rural India has internet access vs. 67% in urban areas",
      "Aadhaar-related exclusion from benefits has affected vulnerable populations",
      "Cybersecurity infrastructure hasn't kept pace with digitization",
      "Digital literacy remains low among older and rural populations",
      "E-governance portals are often poorly designed and inaccessible",
    ],
    discussionPoints: [
      "How to bridge the urban-rural digital divide",
      "Digital literacy as a prerequisite for Digital India's success",
      "Privacy and surveillance concerns with Aadhaar and centralized databases",
      "Role of private tech companies in Digital India's infrastructure",
      "Comparison with China's and Estonia's digital governance models",
    ],
    openingStatements: [
      "India processes 10 billion UPI transactions monthly but has 400 million citizens without internet access. Digital India's success depends on which metric you choose to measure.",
      "Estonia, with a population smaller than Bangalore, is called the world's most digital society. India, with 1.4 billion people, faces a fundamentally different challenge of digital inclusion at scale.",
    ],
    relatedSlugs: ["upi-and-digital-payments-revolution", "data-privacy-concerns", "5g-and-its-impact"],
  },
  {
    slug: "work-life-balance",
    title: "Work-Life Balance in Modern India",
    category: "Social Issues",
    overview: "The debate around work-life balance has intensified in India, with leaders like Narayana Murthy advocating 70-hour work weeks while global trends move toward 4-day work weeks. This topic explores the cultural, economic, and personal dimensions of work-life balance.",
    argumentsFor: [
      "Better work-life balance leads to higher productivity and creativity",
      "Mental health benefits reduce healthcare costs and absenteeism",
      "Companies with flexible policies attract and retain top talent",
      "4-day work week trials in UK and Japan showed no drop in output",
      "Quality of work matters more than quantity of hours",
    ],
    argumentsAgainst: [
      "India's economic stage requires harder work to compete globally",
      "Many successful entrepreneurs attribute success to intense work ethic",
      "Not all industries can accommodate flexibility equally",
      "Generational difference: older leaders see long hours as commitment",
      "Global competition from China and other nations with longer work cultures",
    ],
    discussionPoints: [
      "Is the 70-hour work week debate relevant for all professions?",
      "Cultural differences in work ethic between India and Western countries",
      "Role of technology in enabling or destroying work-life boundaries",
      "Government regulation of work hours vs. market-driven flexibility",
      "Impact on family relationships and personal development",
    ],
    openingStatements: [
      "Narayana Murthy says Indian youth should work 70 hours a week. Microsoft Japan's 4-day work week trial increased productivity by 40%. Both can't be right — or can they?",
      "India has one of the longest average work hours globally, yet ranks 132nd in productivity per hour. Working more isn't the same as working better.",
    ],
    relatedSlugs: ["remote-work-future-of-employment", "mental-health-awareness", "startup-culture-in-india"],
  },
  {
    slug: "5g-and-its-impact",
    title: "5G Technology and Its Impact on India",
    category: "Technology",
    overview: "India launched 5G services in 2022, with Jio and Airtel leading the rollout. 5G promises revolutionary changes in healthcare, manufacturing, education, and entertainment. But questions about infrastructure costs, health concerns, and actual adoption remain.",
    argumentsFor: [
      "100x faster than 4G — enables real-time applications like telemedicine and AR",
      "Industrial IoT and smart manufacturing transform productivity",
      "Enables autonomous vehicles, smart cities, and precision agriculture",
      "India's 5G rollout is one of the fastest globally",
      "Creates new business models and revenue streams for telecom companies",
    ],
    argumentsAgainst: [
      "Infrastructure cost is massive — 10x more towers needed than 4G",
      "Most consumers don't yet have 5G-enabled devices",
      "Use cases beyond faster downloads are still largely theoretical",
      "Rural rollout is slow — deepening the urban-rural digital divide",
      "Health concerns about electromagnetic radiation persist in public discourse",
    ],
    discussionPoints: [
      "Is 5G overhyped for consumer applications?",
      "Enterprise 5G: the real revolution in manufacturing and logistics",
      "Spectrum pricing and its impact on consumer costs",
      "India vs. China and South Korea in 5G adoption",
      "The environmental impact of 5G infrastructure deployment",
    ],
    openingStatements: [
      "South Korea launched 5G in 2019. By 2023, they admitted that most consumers couldn't tell the difference from 4G. Is the consumer 5G hype overblown?",
      "5G isn't about faster YouTube streaming — it's about enabling technologies that don't exist yet. The real impact will be in enterprise, healthcare, and industrial applications.",
    ],
    relatedSlugs: ["digital-india-success-or-failure", "artificial-intelligence-impact-on-jobs", "electric-vehicles-adoption"],
  },
  {
    slug: "education-system-reform",
    title: "Education System Reform in India",
    category: "Social Issues",
    overview: "India's National Education Policy (NEP) 2020 introduced sweeping reforms including a 5+3+3+4 structure, multidisciplinary education, and emphasis on skill-based learning. This topic explores whether NEP can fix India's education challenges.",
    argumentsFor: [
      "NEP replaces outdated rote-learning with skill-based, multidisciplinary education",
      "Mother tongue instruction in early years improves learning outcomes",
      "Multiple entry and exit points in higher education add flexibility",
      "Focus on vocational training bridges the skill gap in employability",
      "Digital education integration prepares students for the modern economy",
    ],
    argumentsAgainst: [
      "Implementation is inconsistent across states and institutions",
      "Infrastructure for NEP's vision is lacking, especially in government schools",
      "Teacher training hasn't kept pace with curriculum changes",
      "Privatization of education risks increasing inequality",
      "Assessment reforms are slow — board exams still dominate evaluation",
    ],
    discussionPoints: [
      "Can NEP solve India's employability crisis?",
      "Role of private EdTech companies in education reform",
      "Comparison with education systems in Finland, Singapore, and the US",
      "Impact of education reform on competitive exam culture (JEE, NEET)",
      "Funding and resource allocation for public education",
    ],
    openingStatements: [
      "India produces 1.5 million engineers annually, but less than 20% are considered employable by industry standards. Our education system produces degrees, not skills. NEP aims to change that.",
      "Finland consistently ranks #1 in education globally with no standardized tests and shorter school days. India's approach of more exams and longer hours is fundamentally different — which model serves students better?",
    ],
    relatedSlugs: ["online-education-vs-offline-education", "reservation-policy-in-india", "mental-health-awareness"],
  },
  {
    slug: "make-in-india-vs-import-dependency",
    title: "Make in India vs Import Dependency",
    category: "Business & Economy",
    overview: "Launched in 2014, Make in India aimed to transform India into a global manufacturing hub. While successes exist in defense, pharmaceuticals, and mobile manufacturing, India still imports heavily in electronics, energy, and industrial goods.",
    argumentsFor: [
      "PLI schemes have attracted $30 billion in manufacturing investments",
      "India is now the world's second-largest mobile phone manufacturer",
      "Defense manufacturing has reduced dependency on foreign arms imports",
      "Pharmaceutical exports make India the 'pharmacy of the world'",
      "Manufacturing sector growth creates jobs for India's large labor force",
    ],
    argumentsAgainst: [
      "China still dominates global manufacturing — India captures only 3% of global share",
      "Infrastructure bottlenecks: poor logistics, expensive power, complex regulations",
      "Skill gap: India's workforce isn't trained for advanced manufacturing",
      "FDI in manufacturing has underperformed compared to services sector",
      "Environmental costs of rapid industrialization aren't being addressed",
    ],
    discussionPoints: [
      "Can India replace China as the world's factory?",
      "Role of automation in India's manufacturing future",
      "Sector-specific success and failure analysis",
      "Impact of geopolitical shifts (China+1 strategy) on India",
      "Balancing manufacturing growth with environmental sustainability",
    ],
    openingStatements: [
      "India imports $70 billion worth of electronics annually while trying to become a manufacturing hub. The Make in India challenge isn't about intent — it's about execution speed.",
      "Apple now manufactures iPhones in India through Foxconn and Tata. This single development captures both the promise and the challenge of Make in India — we're assembling, but are we innovating?",
    ],
    relatedSlugs: ["startup-culture-in-india", "5g-and-its-impact", "climate-change-and-indias-role"],
  },
  // ========== ABSTRACT TOPICS ==========
  {
    slug: "necessity-mother-of-invention",
    title: "Is Necessity the Mother of Invention?",
    category: "Abstract",
    overview: "This classic proverb suggests that difficult situations inspire creative solutions. While history supports this with examples from wartime innovation to pandemic-driven digital transformation, some argue that curiosity and surplus resources drive more meaningful inventions than desperation.",
    argumentsFor: [
      "COVID-19 pandemic led to rapid vaccine development and remote work tools",
      "Resource scarcity in developing nations drives frugal innovation (Jugaad)",
      "Wartime innovations like radar, penicillin, and the internet arose from necessity",
      "Survival pressure accelerates problem-solving and creativity",
      "Constraints force focus and eliminate unnecessary complexity",
    ],
    argumentsAgainst: [
      "Many breakthrough inventions came from curiosity, not necessity — the telescope, X-rays",
      "Well-funded R&D labs (Google, Bell Labs) produce innovations without urgent need",
      "Necessity often leads to quick fixes, not sustainable solutions",
      "Leisure and surplus resources allow for experimentation that necessity doesn't",
      "Some of the greatest art and science emerged from periods of prosperity, not crisis",
    ],
    discussionPoints: [
      "Role of government funding vs. market necessity in driving innovation",
      "Is frugal innovation as impactful as well-funded R&D?",
      "Historical examples: space race, internet, smartphones",
      "How do startups use constraints as competitive advantages?",
      "The difference between invention (creating new) and innovation (improving existing)",
    ],
    openingStatements: [
      "The internet was born from military necessity, but social media was born from Mark Zuckerberg's dorm room curiosity. Both changed the world — so is necessity really the sole driver of invention?",
      "India's Mangalyaan mission succeeded at a fraction of NASA's cost — a perfect example of necessity (budget constraints) mothering invention (frugal engineering).",
    ],
    relatedSlugs: ["startup-culture-in-india", "artificial-intelligence-impact-on-jobs", "climate-change-and-indias-role"],
  },
  {
    slug: "change-is-the-only-constant",
    title: "Change is the Only Constant",
    category: "Abstract",
    overview: "Heraclitus said 'the only constant in life is change.' In today's rapidly evolving world of technology, careers, and social norms, this philosophy is more relevant than ever. But is all change positive? And how should individuals and societies adapt?",
    argumentsFor: [
      "Technological evolution constantly reshapes industries and creates new opportunities",
      "Adaptability is the most valued trait in modern workplaces",
      "Social progress (civil rights, gender equality) comes through change",
      "Companies that resist change (Kodak, Nokia, Blockbuster) fail",
      "Personal growth requires stepping outside comfort zones",
    ],
    argumentsAgainst: [
      "Constant change creates anxiety and decision fatigue",
      "Some traditions and values should remain stable for social cohesion",
      "Not all change is progress — some changes cause harm",
      "The pace of modern change exceeds human adaptive capacity",
      "Stability in institutions (judiciary, education) ensures fairness and consistency",
    ],
    discussionPoints: [
      "How should education systems prepare students for constant change?",
      "Is resistance to change always negative?",
      "The psychological cost of perpetual disruption",
      "Change management in organizations: why 70% of transformations fail",
      "Cultural values that endure despite rapid modernization",
    ],
    openingStatements: [
      "Kodak invented the digital camera but refused to change. Nokia dominated phones but couldn't adapt to smartphones. Change isn't just constant — it's ruthless to those who ignore it.",
      "In the last 20 years, we went from dial-up internet to AI writing essays. The question isn't whether change is constant — it's whether we're changing fast enough.",
    ],
    relatedSlugs: ["artificial-intelligence-impact-on-jobs", "remote-work-future-of-employment", "education-system-reform"],
  },
  {
    slug: "quality-vs-quantity",
    title: "Quality vs Quantity",
    category: "Abstract",
    overview: "The debate between quality and quantity applies across education, business, relationships, and life. Should India produce more engineers or better engineers? Should companies focus on more features or better features? This timeless debate has practical implications.",
    argumentsFor: [
      "Quality products build lasting brand value and customer loyalty",
      "In education, deep understanding beats rote memorization of many topics",
      "Fewer high-quality relationships are more fulfilling than many superficial ones",
      "Quality-focused companies (Apple, Toyota) outperform quantity-focused competitors",
      "Quality work requires fewer corrections and reduces long-term costs",
    ],
    argumentsAgainst: [
      "In early stages (startups, learning), quantity of practice builds quality",
      "Mass production makes essential goods affordable and accessible",
      "India needs more doctors, teachers, and engineers — quantity matters",
      "Data-driven decisions require large quantities of data, not just high-quality data",
      "Quantity provides options and diversity — more experiments mean more breakthroughs",
    ],
    discussionPoints: [
      "India's education: producing 1.5M engineers annually vs. employability crisis",
      "Startup culture: ship fast and iterate vs. build perfect products",
      "Healthcare access: more primary care centers vs. fewer world-class hospitals",
      "Content creation: consistency (quantity) vs. viral hits (quality)",
      "Can you achieve both? Toyota's lean manufacturing as a model",
    ],
    openingStatements: [
      "India produces more engineers than any country except China, yet fewer than 20% are employable. This perfectly illustrates how quantity without quality is waste.",
      "Picasso created over 50,000 artworks in his lifetime. Was it quality or quantity that made him a master? Perhaps the answer is that quantity is the path to quality.",
    ],
    relatedSlugs: ["education-system-reform", "startup-culture-in-india", "make-in-india-vs-import-dependency"],
  },
  {
    slug: "does-money-buy-happiness",
    title: "Does Money Buy Happiness?",
    category: "Abstract",
    overview: "Research shows that money increases happiness up to a point (approximately $75,000/year in the US), after which the correlation weakens. In India, where millions live in poverty, this question has different implications than in wealthy nations.",
    argumentsFor: [
      "Financial security eliminates stress from basic needs — food, shelter, healthcare",
      "Money enables experiences, education, and personal growth opportunities",
      "Economic prosperity correlates with longer life expectancy and better health",
      "Financial freedom allows pursuit of passions and meaningful work",
      "Money can buy time (hiring help, reducing commute) which increases happiness",
    ],
    argumentsAgainst: [
      "Hedonic adaptation: people quickly adjust to higher income levels",
      "Pursuit of wealth often sacrifices relationships, health, and purpose",
      "Some of the happiest countries (Bhutan, Costa Rica) aren't the richest",
      "Social comparison makes the wealthy feel inadequate compared to the ultra-rich",
      "Meaning, purpose, and community contribute more to well-being than income",
    ],
    discussionPoints: [
      "The income-happiness threshold: does it apply in India?",
      "Material wealth vs. emotional wealth in Indian culture",
      "Financial literacy and its role in achieving financial well-being",
      "Impact of consumerism on happiness in developing economies",
      "Can government policies maximize citizen happiness beyond GDP growth?",
    ],
    openingStatements: [
      "A Princeton study found that happiness plateaus at $75,000 annually. But in India, where the average income is $2,500, the relationship between money and happiness is far more direct and urgent.",
      "Bhutan measures Gross National Happiness instead of GDP. India ranks 126th in the World Happiness Report despite being the 5th largest economy. Clearly, money alone doesn't buy national happiness.",
    ],
    relatedSlugs: ["work-life-balance", "mental-health-awareness", "startup-culture-in-india"],
  },
  {
    slug: "leaders-are-born-not-made",
    title: "Leaders are Born, Not Made",
    category: "Abstract",
    overview: "This topic debates whether leadership is an innate quality or a skill that can be developed. While some leaders seem naturally charismatic, modern leadership theory emphasizes that leadership skills can be learned, practiced, and refined.",
    argumentsFor: [
      "Some people exhibit natural charisma, decisiveness, and influence from childhood",
      "Personality traits like extraversion and emotional intelligence have genetic components",
      "Historical leaders like Gandhi, Mandela seemed to have innate moral authority",
      "Not everyone who receives leadership training becomes an effective leader",
      "Natural leaders emerge spontaneously in group settings without formal training",
    ],
    argumentsAgainst: [
      "Leadership styles can be learned through MBA programs, coaching, and mentoring",
      "Situational leadership theory shows different contexts require different skills",
      "Many successful CEOs were introverts who developed leadership abilities (Bill Gates, Satya Nadella)",
      "Military and corporate leadership programs consistently produce effective leaders",
      "Cultural and environmental factors shape leadership more than genetics",
    ],
    discussionPoints: [
      "Nature vs. nurture in leadership development",
      "Are there different types of leadership that suit different personality types?",
      "Role of mentorship and experience in shaping leaders",
      "Leadership in the digital age: has the definition changed?",
      "Gender and leadership: are leadership perceptions biased?",
    ],
    openingStatements: [
      "Satya Nadella was a middle manager at Microsoft for 22 years before becoming CEO and transforming the company. If leaders were only born, not made, he would never have grown into the role.",
      "A study of identical twins showed that only 30% of leadership ability is genetic. That means 70% is shaped by experience, training, and environment.",
    ],
    relatedSlugs: ["gender-equality-in-workplaces", "education-system-reform", "work-life-balance"],
  },
  {
    slug: "pen-mightier-than-sword",
    title: "The Pen is Mightier Than the Sword",
    category: "Abstract",
    overview: "This proverb suggests that written words and ideas have more lasting influence than military force. In the age of social media, misinformation, and digital activism, the power of words has taken new dimensions.",
    argumentsFor: [
      "Gandhi's writings and speeches mobilized millions without violence",
      "Social media movements (#MeToo, Arab Spring) created change through words",
      "Constitutions and laws shape societies more permanently than wars",
      "Investigative journalism has toppled corrupt regimes (Watergate, Panama Papers)",
      "Education and literacy are the foundations of sustainable development",
    ],
    argumentsAgainst: [
      "Military power ultimately enforces laws and protects nations",
      "Words without enforcement mechanisms are ineffective (UN resolutions ignored)",
      "Misinformation and propaganda show words can be weapons of destruction too",
      "Many freedom movements required armed resistance, not just words",
      "In immediate crisis situations, action matters more than rhetoric",
    ],
    discussionPoints: [
      "Role of media in modern democracy — fourth pillar or corporate tool?",
      "Fake news and the weaponization of words in the digital age",
      "Censorship vs. free speech in the context of this proverb",
      "Can social media activism create real change without ground-level action?",
      "The power of storytelling in business and leadership",
    ],
    openingStatements: [
      "One tweet by Elon Musk can move markets by billions of dollars. In the digital age, the pen — or rather, the keyboard — is indeed mightier than any sword.",
      "Gandhi never picked up a weapon, yet he helped free a nation of 400 million people. His pen, and his ideas, proved mightier than the British Empire's sword.",
    ],
    relatedSlugs: ["social-media-advantages-and-disadvantages", "social-media-regulation", "data-privacy-concerns"],
  },
  // ========== MORE CURRENT AFFAIRS ==========
  {
    slug: "social-media-regulation",
    title: "Should Social Media Be Regulated?",
    category: "Current Affairs",
    overview: "Governments worldwide are debating how to regulate social media platforms. India's IT Rules 2021 require content moderation and compliance from platforms. The challenge is balancing free speech with preventing harm from misinformation, hate speech, and manipulation.",
    argumentsFor: [
      "Misinformation on social media has caused mob violence and deaths in India",
      "Platforms profit from engagement algorithms that amplify divisive content",
      "Children and teenagers need protection from harmful content and addiction",
      "Traditional media is regulated — digital platforms should be too",
      "EU's Digital Services Act provides a model for accountable platform governance",
    ],
    argumentsAgainst: [
      "Government regulation can become censorship and suppress dissent",
      "Defining harmful content is subjective and culturally variable",
      "Over-regulation stifles innovation and startup growth in tech",
      "Platforms are already implementing self-regulation through content policies",
      "Internet shutdowns in India show governments misuse regulatory power",
    ],
    discussionPoints: [
      "India's IT Rules 2021: effective regulation or government overreach?",
      "Who decides what content is harmful — government, platforms, or users?",
      "Impact of regulation on startup ecosystem and digital economy",
      "Section 230 debate in the US and its relevance for India",
      "Self-regulation vs. government regulation: which is more effective?",
    ],
    openingStatements: [
      "India had 84 internet shutdowns in 2023 alone — more than any other democracy. The question isn't whether social media should be regulated, but who gets to regulate it and how.",
      "Facebook's own research showed Instagram harms teenage mental health, but the company buried the findings. When platforms prioritize profits over safety, regulation becomes necessary.",
    ],
    relatedSlugs: ["social-media-advantages-and-disadvantages", "data-privacy-concerns", "digital-india-success-or-failure"],
  },
  {
    slug: "india-global-superpower",
    title: "Can India Become a Global Superpower?",
    category: "Current Affairs",
    overview: "With the world's largest population, 5th largest economy, and growing geopolitical influence, India's superpower ambitions are debated globally. But challenges in poverty, infrastructure, and governance remain significant barriers.",
    argumentsFor: [
      "Youngest population globally — demographic dividend until 2050",
      "5th largest economy, projected to become 3rd by 2030",
      "Growing defense capabilities and space program (Chandrayaan, Gaganyaan)",
      "Strategic geopolitical position between US, China, and Russia",
      "Soft power through diaspora, Bollywood, yoga, and cultural exports",
    ],
    argumentsAgainst: [
      "800 million people still depend on free food grains from the government",
      "Infrastructure gap: roads, railways, healthcare lag behind China's development",
      "Education and skill development aren't producing a competitive workforce",
      "Internal challenges: communal tensions, caste divisions, regional disparities",
      "Bureaucratic inefficiency and corruption slow economic progress",
    ],
    discussionPoints: [
      "Military power vs. economic power vs. soft power — what defines a superpower?",
      "India's China strategy: competitor, partner, or cautious neighbor?",
      "Role of the Indian diaspora in global influence",
      "Can India leapfrog infrastructure challenges using technology?",
      "Comparison with China's rise: lessons and differences",
    ],
    openingStatements: [
      "India sent a mission to the Moon's south pole for $75 million — less than the budget of the movie Interstellar. If that's not superpower potential, what is?",
      "India's GDP grows at 7% annually, but 22% of its population lives below the poverty line. A superpower with hungry citizens is a contradiction we must resolve.",
    ],
    relatedSlugs: ["make-in-india-vs-import-dependency", "digital-india-success-or-failure", "climate-change-and-indias-role"],
  },
  {
    slug: "farm-laws-and-agricultural-reform",
    title: "Farm Laws and Agricultural Reform in India",
    category: "Current Affairs",
    overview: "India's agricultural sector employs 42% of the workforce but contributes only 18% to GDP. The controversial farm laws of 2020, their repeal, and the ongoing need for agricultural reform remain central to India's economic and social policy.",
    argumentsFor: [
      "Agricultural reforms are needed — MSP system benefits only 6% of farmers",
      "Contract farming and corporate investment can modernize agriculture",
      "Technology adoption (drones, precision farming) requires structural reform",
      "Post-harvest losses of 30-40% need cold chain and market infrastructure",
      "Global examples (Israel, Netherlands) show technology-driven agriculture works",
    ],
    argumentsAgainst: [
      "Small farmers (86% of Indian farmers) fear corporate exploitation without MSP guarantee",
      "The 2020 farm laws were passed without adequate consultation with farmer unions",
      "APMC mandi system, despite flaws, provides a safety net for farmers",
      "Land consolidation could displace millions of small and marginal farmers",
      "Farmer suicides (over 10,000 annually) show the sector's crisis isn't just policy",
    ],
    discussionPoints: [
      "Can technology solve Indian agriculture's problems without structural reform?",
      "Role of FPOs (Farmer Producer Organizations) in empowering small farmers",
      "Water crisis and its impact on Indian agriculture",
      "Organic farming vs. conventional farming: what's right for India?",
      "Lessons from the 2020-21 farmer protests for democratic policymaking",
    ],
    openingStatements: [
      "India feeds 1.4 billion people but 42% of its food is wasted before reaching consumers. Our agricultural crisis isn't about production — it's about the supply chain.",
      "The Netherlands, a country smaller than Rajasthan, is the world's second-largest food exporter. India, with 10x the farmland, struggles with food inflation. The difference is technology and policy.",
    ],
    relatedSlugs: ["climate-change-and-indias-role", "make-in-india-vs-import-dependency", "india-global-superpower"],
  },
  {
    slug: "uniform-civil-code",
    title: "Uniform Civil Code in India",
    category: "Current Affairs",
    overview: "The Uniform Civil Code (UCC) proposes replacing personal laws of various religious communities with a common set of laws governing marriage, divorce, inheritance, and adoption. Article 44 of the Indian Constitution lists it as a Directive Principle.",
    argumentsFor: [
      "Constitutional directive: Article 44 recommends UCC as a goal of state policy",
      "Ensures gender equality by removing discriminatory personal laws across religions",
      "Simplifies the legal system with one law for all citizens",
      "Promotes national integration and equal citizenship",
      "Goa already has a functioning common civil code as a precedent",
    ],
    argumentsAgainst: [
      "India's diversity makes one-size-fits-all personal law impractical",
      "Threatens religious freedom and cultural identity of minorities",
      "Implementation challenges in a country with diverse customs and traditions",
      "Reform within personal laws (like Muslim women's rights) may be more effective",
      "Political weaponization of UCC creates communal tensions",
    ],
    discussionPoints: [
      "Is UCC about gender justice or majoritarian imposition?",
      "Lessons from Goa's common civil code",
      "Role of the judiciary in personal law reform (Shah Bano, Triple Talaq)",
      "How other secular democracies handle personal laws",
      "Phased implementation vs. one-time adoption",
    ],
    openingStatements: [
      "Dr. Ambedkar, who drafted our Constitution, placed the Uniform Civil Code in Article 44 as an aspiration. 76 years later, we're still debating whether India is ready for it.",
      "In India, the same act — say, a man abandoning his wife — has different legal consequences depending on his religion. A Uniform Civil Code asks: should citizenship, not religion, define your legal rights?",
    ],
    relatedSlugs: ["reservation-policy-in-india", "gender-equality-in-workplaces", "india-global-superpower"],
  },
  // ========== MORE BUSINESS & ECONOMY ==========
  {
    slug: "fdi-in-indian-retail",
    title: "FDI in Indian Retail Sector",
    category: "Business & Economy",
    overview: "Foreign Direct Investment in India's retail sector is a contentious topic. While FDI brings capital, technology, and efficiency, concerns about displacing millions of small shopkeepers and kirana stores persist. India allows 100% FDI in marketplace-based e-commerce but restricts inventory-based models.",
    argumentsFor: [
      "FDI brings world-class supply chain infrastructure and cold storage",
      "Consumers benefit from lower prices, more choices, and better quality",
      "Creates organized sector jobs in logistics, warehousing, and retail operations",
      "Technology transfer improves efficiency across the retail ecosystem",
      "Successful integration in countries like China and Brazil provides precedent",
    ],
    argumentsAgainst: [
      "India has 80 million small retail stores — FDI threatens their livelihood",
      "Predatory pricing by well-funded foreign retailers can destroy local competition",
      "Profits are repatriated, not reinvested in the Indian economy",
      "India's retail diversity and neighborhood commerce is a cultural strength",
      "Regulatory loopholes allow foreign companies to bypass FDI restrictions",
    ],
    discussionPoints: [
      "Can kirana stores coexist with modern retail through technology adoption?",
      "Impact of Amazon and Flipkart on small retailers",
      "Should India follow China's model of selective FDI liberalization?",
      "Role of ONDC (Open Network for Digital Commerce) in leveling the playing field",
      "Employment quality: organized retail jobs vs. self-employed shopkeepers",
    ],
    openingStatements: [
      "India's 80 million kirana stores serve 90% of the retail market. When Walmart entered, many feared their extinction. Instead, technology platforms are helping kiranas compete. The story is more nuanced than FDI vs. local.",
      "Amazon invested $6.5 billion in India but still struggles with profitability. Meanwhile, ONDC aims to create an open, democratic e-commerce network. India is writing its own retail playbook.",
    ],
    relatedSlugs: ["startup-culture-in-india", "make-in-india-vs-import-dependency", "digital-india-success-or-failure"],
  },
  {
    slug: "cashless-economy",
    title: "Is India Ready for a Cashless Economy?",
    category: "Business & Economy",
    overview: "India's digital payments have exploded post-demonetization and UPI adoption. But with 65% of transactions still in cash and significant digital divide, the question of whether India can — or should — go fully cashless remains relevant.",
    argumentsFor: [
      "UPI processes 10 billion+ monthly transactions — infrastructure is ready",
      "Cashless transactions reduce corruption, tax evasion, and black money",
      "Digital payments are more efficient, safer, and easier to track",
      "Post-COVID behavior shift has increased digital payment adoption",
      "Government push through Jan Dhan accounts and Aadhaar enables financial inclusion",
    ],
    argumentsAgainst: [
      "35% of India's population lacks internet access for digital payments",
      "Cash is essential for privacy — not all transactions should be trackable",
      "System outages and technical failures can paralyze a cashless economy",
      "Elderly and low-literacy populations struggle with digital interfaces",
      "Cybersecurity risks increase with greater digital transaction volume",
    ],
    discussionPoints: [
      "Demonetization's impact: did it accelerate or temporarily spike digital adoption?",
      "Role of offline payment solutions (USSD, Aadhaar Pay) for rural India",
      "Privacy implications of a fully cashless, trackable economy",
      "Comparison with Sweden and China's cashless transitions",
      "Should India aim for 'less cash' rather than 'cashless'?",
    ],
    openingStatements: [
      "Sweden plans to go fully cashless by 2030. India, with 10x the complexity, is attempting something similar through UPI. But can a country where street vendors sell chai for ₹10 go entirely digital?",
      "India went from 2% digital transactions to 40% in just 6 years. At this rate, we won't need to mandate cashless — the market will get there on its own.",
    ],
    relatedSlugs: ["upi-and-digital-payments-revolution", "digital-india-success-or-failure", "data-privacy-concerns"],
  },
  {
    slug: "privatization-of-public-sector",
    title: "Privatization of Public Sector Enterprises",
    category: "Business & Economy",
    overview: "India's government is actively pursuing disinvestment and privatization of public sector units (PSUs) like Air India, BPCL, and LIC. Supporters cite efficiency gains while critics worry about job losses and public asset undervaluation.",
    argumentsFor: [
      "Air India's turnaround under Tata demonstrates privatization benefits",
      "Private sector is generally more efficient, innovative, and customer-focused",
      "Government should focus on governance, not running businesses",
      "Disinvestment revenue funds social welfare programs",
      "Competition from private sector improves service quality for consumers",
    ],
    argumentsAgainst: [
      "PSUs serve social objectives that private companies won't prioritize",
      "Privatization often leads to layoffs and loss of employee benefits",
      "Public assets built over decades are sold at undervalued prices",
      "Essential services (water, railways) shouldn't be profit-driven",
      "Private monopolies can be worse than public monopolies",
    ],
    discussionPoints: [
      "Which sectors should remain public and which should be privatized?",
      "Employee protection in privatization deals",
      "Strategic disinvestment vs. full privatization",
      "Lessons from UK's railway privatization and water privatization",
      "Role of regulatory bodies in preventing private monopoly abuse",
    ],
    openingStatements: [
      "Air India went from losing ₹20 crore daily as a government airline to ordering 500 new aircraft under Tata ownership. If that's not an argument for privatization, what is?",
      "When British Rail was privatized, fares increased 20% and service quality declined. Privatization isn't automatically better — it depends on regulation and accountability.",
    ],
    relatedSlugs: ["startup-culture-in-india", "make-in-india-vs-import-dependency", "gig-economy-and-worker-rights"],
  },
  // ========== MORE SOCIAL ISSUES ==========
  {
    slug: "rural-vs-urban-development",
    title: "Rural vs Urban Development in India",
    category: "Social Issues",
    overview: "India is urbanizing rapidly — 35% of the population lives in cities, projected to reach 50% by 2050. But rural India, home to 900 million people, lags in infrastructure, healthcare, and economic opportunities. Balanced development is critical.",
    argumentsFor: [
      "Urban areas generate 63% of India's GDP and drive economic growth",
      "Agglomeration benefits: cities enable innovation, collaboration, and efficiency",
      "Smart city initiatives bring technology-driven governance to urban areas",
      "Urban migration provides better employment and education opportunities",
      "Infrastructure investment in cities has higher economic multiplier effects",
    ],
    argumentsAgainst: [
      "900 million rural Indians lack adequate healthcare, education, and roads",
      "Urban-centric development causes unplanned migration and slum growth",
      "Rural economy (agriculture, dairy, handicrafts) supports majority of employment",
      "Environmental degradation is worse in rapidly urbanizing cities",
      "The digital divide between rural and urban India is widening, not closing",
    ],
    discussionPoints: [
      "Can technology bridge the rural-urban gap? (telemedicine, online education, digital banking)",
      "Role of MGNREGA and rural employment schemes",
      "Smart villages vs. smart cities: where should India invest?",
      "Impact of COVID-19 reverse migration on rural development priorities",
      "The concept of rurbanization — blending rural and urban advantages",
    ],
    openingStatements: [
      "Mumbai generates more GDP than Sri Lanka, while Bihar's per capita income is lower than Sub-Saharan Africa's average. This isn't two countries — it's one India with two realities.",
      "During COVID-19, millions of migrant workers walked hundreds of kilometers from cities to villages. It exposed a fundamental truth: urban India was built on the labor of rural India without building rural India itself.",
    ],
    relatedSlugs: ["digital-india-success-or-failure", "education-system-reform", "farm-laws-and-agricultural-reform"],
  },
  {
    slug: "population-boon-or-bane",
    title: "India's Population: Boon or Bane?",
    category: "Social Issues",
    overview: "India surpassed China as the world's most populous country in 2023 with 1.44 billion people. Whether this massive population is a demographic dividend or a disaster depends on education, healthcare, and employment capacity.",
    argumentsFor: [
      "Young population (median age 28) provides demographic dividend for economic growth",
      "Large consumer market attracts foreign investment and domestic entrepreneurship",
      "India can supply the global workforce as developed nations age (Japan, Germany)",
      "Cultural diversity and talent pool drive innovation and creativity",
      "Population scale enables cost-effective solutions (UPI, Aadhaar) not possible elsewhere",
    ],
    argumentsAgainst: [
      "Resources per capita decrease — water, food, energy become scarce",
      "Unemployment: India needs 10 million new jobs annually but creates far fewer",
      "Overcrowded cities with inadequate infrastructure and housing",
      "Environmental strain: more people means more pollution and deforestation",
      "Quality of life suffers when population outpaces service delivery capacity",
    ],
    discussionPoints: [
      "Demographic dividend: how does India convert young population into economic advantage?",
      "Population policy: incentives vs. mandates vs. education",
      "Fertility rate decline: India's TFR is already below replacement in many states",
      "Comparison with China's one-child policy and its consequences",
      "Ageing in southern states vs. youth bulge in northern states",
    ],
    openingStatements: [
      "India has more people under 25 than the entire population of the United States. If we educate and employ them, it's a demographic dividend. If we don't, it's a demographic disaster.",
      "Japan's population is declining and it struggles to fill jobs. India's population is growing and it struggles to create jobs. Both have population problems — but of opposite kinds.",
    ],
    relatedSlugs: ["india-global-superpower", "education-system-reform", "rural-vs-urban-development"],
  },
  {
    slug: "nuclear-family-vs-joint-family",
    title: "Nuclear Family vs Joint Family System",
    category: "Social Issues",
    overview: "India's traditional joint family system is giving way to nuclear families, especially in urban areas. While joint families offer support and security, nuclear families provide independence and privacy. This shift has significant social and economic implications.",
    argumentsFor: [
      "Nuclear families provide privacy, independence, and personal growth",
      "Decision-making is faster and simpler with fewer family members",
      "Children in nuclear families often receive more focused attention",
      "Modern careers requiring relocation make joint families impractical",
      "Individual identity and personal boundaries are better maintained",
    ],
    argumentsAgainst: [
      "Joint families provide built-in childcare and elderly care support",
      "Shared expenses reduce financial burden on individual earning members",
      "Emotional support system helps cope with stress and challenges",
      "Cultural values and traditions are better preserved in joint families",
      "Loneliness and isolation are growing problems in nuclear family setups",
    ],
    discussionPoints: [
      "Impact of urbanization on family structure",
      "Old age care crisis in nuclear family-dominant societies",
      "Can technology (video calls, group chats) preserve joint family bonds?",
      "Financial implications: shared housing costs vs. individual ownership",
      "Mental health in nuclear families vs. joint families",
    ],
    openingStatements: [
      "Japan has 'kodokushi' — lonely deaths where people die alone and aren't discovered for weeks. As India moves toward nuclear families, are we heading toward a similar isolation crisis?",
      "In a joint family, a child has 10 role models. In a nuclear family, a child has 2. But in a nuclear family, those 2 can give undivided attention. The debate is about trade-offs, not absolutes.",
    ],
    relatedSlugs: ["work-life-balance", "mental-health-awareness", "gender-equality-in-workplaces"],
  },
  // ========== MORE TECHNOLOGY ==========
  {
    slug: "space-exploration-worth-the-cost",
    title: "Is Space Exploration Worth the Cost?",
    category: "Technology",
    overview: "India's ISRO has achieved remarkable feats with frugal budgets — Chandrayaan-3 cost less than the movie Gravity. But critics question whether space spending is justified when millions lack basic necessities. This topic explores the value proposition of space exploration.",
    argumentsFor: [
      "ISRO's budget is just 0.04% of India's GDP — minimal cost, massive returns",
      "Space technology enables weather forecasting, disaster management, and navigation",
      "Chandrayaan and Mangalyaan boosted India's global scientific prestige",
      "Space industry creates high-skilled jobs and stimulates STEM education",
      "Satellite-based internet can bridge India's rural connectivity gap",
    ],
    argumentsAgainst: [
      "₹600 crore on Chandrayaan-3 could fund thousands of rural schools",
      "Basic needs (clean water, healthcare, sanitation) should come before space ambitions",
      "Private companies (SpaceX, Blue Origin) are already commercializing space",
      "India's space achievements don't directly improve common citizens' daily lives",
      "Risk of space debris and environmental impact of rocket launches",
    ],
    discussionPoints: [
      "ISRO's cost efficiency vs. NASA and ESA — lessons in frugal innovation",
      "Commercial space industry (Skyroot, Agnikul) and India's opportunity",
      "Space diplomacy: how ISRO strengthens India's geopolitical position",
      "Satellite internet (Starlink) vs. terrestrial connectivity for rural India",
      "Should ISRO focus on human spaceflight or satellite services?",
    ],
    openingStatements: [
      "ISRO's Chandrayaan-3 landed on the Moon's south pole for ₹600 crore — less than what Delhi Metro spends in a month. The argument that India can't afford space exploration doesn't hold up to the numbers.",
      "Every weather forecast you check, every GPS navigation you use, every satellite TV channel you watch — all depend on space technology. Space exploration isn't a luxury; it's infrastructure.",
    ],
    relatedSlugs: ["india-global-superpower", "artificial-intelligence-impact-on-jobs", "5g-and-its-impact"],
  },
  {
    slug: "quantum-computing-future",
    title: "Quantum Computing: The Next Technological Revolution",
    category: "Technology",
    overview: "Quantum computing promises to solve problems that classical computers can't — from drug discovery to cryptography. India has invested ₹6,000 crore in its National Quantum Mission. But the technology is still in its infancy.",
    argumentsFor: [
      "Can solve complex optimization problems in logistics, finance, and healthcare",
      "Drug discovery timelines could shrink from years to months",
      "India's National Quantum Mission positions the country as an early adopter",
      "Quantum cryptography can create unhackable communication systems",
      "First-mover advantage in quantum tech could define the next tech superpower",
    ],
    argumentsAgainst: [
      "Practical quantum computers are decades away from widespread use",
      "Quantum computing threatens current encryption, risking global cybersecurity",
      "Requires extreme conditions (near absolute zero temperatures) to operate",
      "India lacks the quantum talent pool — only a handful of researchers globally",
      "Investment may not yield returns for 20-30 years — risky for developing nations",
    ],
    discussionPoints: [
      "Is India investing enough in quantum research compared to US, China, and EU?",
      "Quantum computing vs. classical computing: where's the crossover point?",
      "Impact on cybersecurity and the need for post-quantum encryption",
      "Academic-industry collaboration in quantum technology development",
      "Should India focus on quantum computing or quantum communication first?",
    ],
    openingStatements: [
      "Google claimed quantum supremacy in 2019, solving in 200 seconds what would take a supercomputer 10,000 years. If that's even partially true, quantum computing will redefine what's computationally possible.",
      "India's ₹6,000 crore quantum mission sounds impressive until you compare it with China's $15 billion investment. In the quantum race, the gap between leaders and laggards could define the 21st century.",
    ],
    relatedSlugs: ["artificial-intelligence-impact-on-jobs", "ethical-ai-development", "5g-and-its-impact"],
  },
  {
    slug: "cybersecurity-threats-india",
    title: "Cybersecurity Threats to India",
    category: "Technology",
    overview: "As India digitizes everything from banking to governance, cybersecurity threats are escalating. India reported 13.9 lakh cybersecurity incidents in 2022. From AIIMS ransomware attacks to Aadhaar data leaks, the stakes are higher than ever.",
    argumentsFor: [
      "Growing awareness: India's CERT-In is increasingly proactive in threat detection",
      "Cybersecurity industry in India is growing at 30% annually, creating jobs",
      "Government mandates like the 6-hour incident reporting rule improve response",
      "Bug bounty programs and ethical hacking communities strengthen defenses",
      "India's cybersecurity workforce is projected to reach 1 million by 2025",
    ],
    argumentsAgainst: [
      "India faces a 30% shortage of cybersecurity professionals",
      "Most Indian SMEs lack basic cybersecurity measures",
      "Government systems (AIIMS, power grids) remain vulnerable to attacks",
      "Aadhaar's centralized database is a single point of failure",
      "Cyber laws are outdated — IT Act 2000 doesn't address modern threats adequately",
    ],
    discussionPoints: [
      "Should cybersecurity be treated as national security priority?",
      "Role of AI in cybersecurity: defense and offense",
      "Privacy vs. security: government access to encrypted communications",
      "India's vulnerability to state-sponsored cyberattacks",
      "Cybersecurity education and curriculum in Indian universities",
    ],
    openingStatements: [
      "When AIIMS Delhi — India's premier hospital — was hit by ransomware, patient records were held hostage for weeks. If we can't protect our hospitals digitally, how can we protect a digital economy?",
      "India is the world's second-largest internet market but ranks 10th in cybersecurity preparedness. This gap between digital ambition and digital security is a ticking time bomb.",
    ],
    relatedSlugs: ["data-privacy-concerns", "digital-india-success-or-failure", "5g-and-its-impact"],
  },
  // ========== MORE EDUCATION ==========
  {
    slug: "competitive-exam-culture-india",
    title: "India's Competitive Exam Culture: Help or Harm?",
    category: "Education",
    overview: "India's obsession with competitive exams (JEE, NEET, UPSC, CAT) shapes the lives of millions of students. The coaching industry is worth ₹60,000+ crore. While these exams provide a merit-based selection system, the pressure and narrow focus raise serious concerns.",
    argumentsFor: [
      "Competitive exams provide a merit-based, objective selection system",
      "Prepare students for high-pressure, real-world professional environments",
      "Coaching industry creates employment for thousands of teachers and staff",
      "Success in competitive exams opens doors to career stability and social mobility",
      "Standardized testing ensures fairness across diverse educational backgrounds",
    ],
    argumentsAgainst: [
      "Extreme pressure leads to student suicides — Kota alone reports dozens annually",
      "Focus on exam-specific preparation kills genuine curiosity and creativity",
      "Coaching industry creates inequality — poor families can't afford ₹5 lakh fees",
      "One exam on one day determining a student's entire future is fundamentally unfair",
      "Students spend 2-3 years preparing for exams instead of developing actual skills",
    ],
    discussionPoints: [
      "Can India move from one-shot exams to continuous assessment?",
      "Kota's coaching culture: success factory or pressure cooker?",
      "Impact of CUET on undergraduate admissions",
      "NEP's proposed reforms in assessment: will they work?",
      "International comparison: SAT, A-Levels, Gaokao vs. JEE/NEET",
    ],
    openingStatements: [
      "2.4 million students appear for JEE, but only 16,000 get into IITs. That's a 0.67% success rate. We've created a system where 99.3% are told they 'failed' at age 18.",
      "India's coaching industry is worth ₹60,000 crore — more than the GDP of several Indian states. We've built an entire economy around exam anxiety. Is that education or exploitation?",
    ],
    relatedSlugs: ["education-system-reform", "mental-health-awareness", "online-education-vs-offline-education"],
  },
  {
    slug: "skill-based-education",
    title: "Skill-Based Education vs Traditional Degrees",
    category: "Education",
    overview: "With only 45% of Indian graduates being employable, the gap between academic credentials and actual skills is widening. Germany's dual education system, vocational training, and skill-based certifications offer alternative models.",
    argumentsFor: [
      "Skill-based education directly addresses India's employability crisis",
      "Vocational training creates job-ready professionals faster than 4-year degrees",
      "Germany's dual education model shows skill-based learning drives manufacturing excellence",
      "Technology certifications (AWS, Google) are increasingly valued over degrees",
      "NEP 2020 integrates vocational training from Class 6 onward",
    ],
    argumentsAgainst: [
      "Indian society still stigmatizes vocational education as 'less prestigious'",
      "Degrees provide broader knowledge and critical thinking abilities",
      "Skills become obsolete quickly — foundational education has longer relevance",
      "Quality of skill training in India is inconsistent and poorly regulated",
      "Skill-based jobs often pay less than knowledge-economy jobs requiring degrees",
    ],
    discussionPoints: [
      "How does Germany's apprenticeship model work and can India adapt it?",
      "Role of industry in co-designing curricula with educational institutions",
      "Micro-credentials and stackable certifications as alternatives to degrees",
      "Should coding be taught in all schools regardless of stream?",
      "Impact of AI on which skills will remain relevant",
    ],
    openingStatements: [
      "Google, Apple, and IBM no longer require college degrees for many positions. If the world's biggest companies don't value degrees, why does India's education system revolve around them?",
      "Germany has youth unemployment of 5.8%. India's is 23.2%. The key difference? Germany integrates vocational training into its education system. India treats it as a consolation prize.",
    ],
    relatedSlugs: ["education-system-reform", "artificial-intelligence-impact-on-jobs", "competitive-exam-culture-india"],
  },
  // ========== ADDITIONAL TOPICS FOR BREADTH ==========
  {
    slug: "death-penalty-justified",
    title: "Is the Death Penalty Justified?",
    category: "Abstract",
    overview: "India retains the death penalty for the 'rarest of rare' cases, while over 100 countries have abolished it. This topic examines whether capital punishment serves as a deterrent, whether it's morally justifiable, and whether India should reconsider its position.",
    argumentsFor: [
      "Acts as a deterrent for heinous crimes like terrorism and sexual assault of minors",
      "Provides justice and closure to victims' families in extreme cases",
      "Some crimes are so severe that no rehabilitation is possible",
      "The Supreme Court's 'rarest of rare' doctrine limits its use to exceptional cases",
      "Democratic mandate: public opinion in India largely supports capital punishment",
    ],
    argumentsAgainst: [
      "No conclusive evidence that death penalty deters crime more than life imprisonment",
      "Risk of executing innocent people — wrongful convictions happen",
      "Disproportionately applied to poor, lower-caste, and minority defendants",
      "Violates the fundamental right to life enshrined in Article 21",
      "Global trend is toward abolition — over 70% of countries have abolished or suspended it",
    ],
    discussionPoints: [
      "Should India follow the global trend toward abolition?",
      "Alternatives to death penalty: life without parole, restorative justice",
      "The 'rarest of rare' doctrine: has it been applied consistently?",
      "Role of mercy petitions and presidential pardons",
      "Impact on the mental health of prisoners on death row",
    ],
    openingStatements: [
      "India's last execution was in 2020 — the Nirbhaya case convicts. The case took 7 years from crime to execution. If the death penalty is meant to deter, does a 7-year delay serve that purpose?",
      "The Innocence Project in the US has exonerated 375 wrongfully convicted individuals, including 21 who were on death row. Can any legal system be accurate enough to justify irreversible punishment?",
    ],
    relatedSlugs: ["reservation-policy-in-india", "uniform-civil-code", "gender-equality-in-workplaces"],
  },
  {
    slug: "media-trial-vs-fair-trial",
    title: "Media Trial vs Fair Trial",
    category: "Social Issues",
    overview: "In high-profile cases, media coverage often creates a parallel trial that influences public opinion and potentially judicial outcomes. From the Arushi Talwar case to the SSR case, media trials have raised questions about press freedom, judicial independence, and the right to fair trial.",
    argumentsFor: [
      "Media scrutiny keeps the justice system accountable and transparent",
      "Public awareness of cases can lead to faster justice and policy changes",
      "Investigative journalism has uncovered police negligence and corruption",
      "Free press is essential for democracy — restrictions risk censorship",
      "Media coverage can protect accused from wrongful prosecution by creating public record",
    ],
    argumentsAgainst: [
      "Media trials create mob mentality and convict people before judgment",
      "Sensationalized coverage violates the accused's right to presumption of innocence",
      "TRP-driven journalism prioritizes drama over facts",
      "Judges and witnesses can be influenced by pervasive media narratives",
      "Character assassination by media is irreversible even if the accused is acquitted",
    ],
    discussionPoints: [
      "Should courts impose gag orders in high-profile cases?",
      "Social media's amplification of media trial effects",
      "The difference between reporting on a case and conducting a trial",
      "International models of restricting prejudicial media coverage",
      "Impact on the mental health and reputation of the accused and their families",
    ],
    openingStatements: [
      "In the Aarushi Talwar case, the parents were convicted by media long before the courts acquitted them. Their reputation was destroyed by coverage that turned speculation into truth.",
      "The Nirbhaya case showed media at its best — creating public pressure that led to stronger laws. The SSR case showed media at its worst — creating conspiracy theories that hindered investigation. Same tool, opposite outcomes.",
    ],
    relatedSlugs: ["social-media-regulation", "social-media-advantages-and-disadvantages", "data-privacy-concerns"],
  },
  {
    slug: "sports-beyond-cricket",
    title: "India's Obsession with Cricket: Time for Change?",
    category: "Social Issues",
    overview: "Cricket dominates Indian sports culture, attracting 90%+ of sponsorship money. Meanwhile, Olympic athletes train with minimal support. India's recent successes in wrestling, badminton, and hockey raise the question: should India diversify its sporting culture?",
    argumentsFor: [
      "Neeraj Chopra, PV Sindhu, and Mary Kom proved India can excel in non-cricket sports",
      "Olympic success brings global recognition that cricket's bilateral format doesn't",
      "Diversifying sports funding can uncover talent in 1.4 billion people",
      "Sports like football, basketball, and athletics have larger global audiences",
      "Khelo India and Target Olympic Podium schemes show government is shifting focus",
    ],
    argumentsAgainst: [
      "Cricket generates the revenue that funds other sports through BCCI contributions",
      "Market-driven: audiences choose cricket, and advertisers follow audiences",
      "IPL's success model can be replicated for other sports (PKL, ISL have tried)",
      "Forcing sports diversification in a free market is impractical",
      "Cricket infrastructure and grassroots programs are too entrenched to displace",
    ],
    discussionPoints: [
      "How does China's Olympic medal factory model compare to India's approach?",
      "Role of corporate sponsorship in developing non-cricket sports",
      "School-level sports infrastructure and talent identification",
      "Impact of IPL-style leagues (PKL, ISL, PBL) on other sports' popularity",
      "Should India bid for the Olympics to boost non-cricket sports investment?",
    ],
    openingStatements: [
      "India has 1.4 billion people and won 7 Olympic medals in Tokyo. Jamaica, with 3 million people, won 9. Our problem isn't talent — it's where we invest our sporting resources.",
      "The BCCI's annual revenue exceeds the combined budget of all other Indian sports federations. When one sport captures 90% of resources, we're not choosing cricket — we're neglecting everything else.",
    ],
    relatedSlugs: ["india-global-superpower", "education-system-reform", "population-boon-or-bane"],
  },
  {
    slug: "tourism-potential-india",
    title: "India's Untapped Tourism Potential",
    category: "Business & Economy",
    overview: "India has 40 UNESCO World Heritage Sites but attracts only 10 million international tourists annually — less than Thailand (40 million) or France (90 million). Tourism contributes just 5% to India's GDP despite enormous potential.",
    argumentsFor: [
      "Incredible diversity: heritage, spirituality, adventure, wildlife, beaches, mountains",
      "Tourism creates employment in rural and semi-urban areas where jobs are scarce",
      "Medical tourism alone is worth $9 billion and growing rapidly",
      "Weak rupee makes India affordable for international travelers",
      "Government's 'Incredible India' campaign has strong global brand recognition",
    ],
    argumentsAgainst: [
      "Poor infrastructure: bad roads, unhygienic public facilities, safety concerns",
      "Tourist scams and harassment deter repeat visitors and damage reputation",
      "Visa processes are complex compared to Thailand or Indonesia",
      "Over-tourism at popular sites (Taj Mahal, Goa) while lesser-known sites languish",
      "Environmental damage from unregulated tourism in fragile ecosystems",
    ],
    discussionPoints: [
      "How can India compete with Southeast Asian countries for tourist spending?",
      "Role of state governments in tourism development",
      "Sustainable tourism vs. mass tourism models",
      "Safety of women travelers and its impact on India's tourism image",
      "Heritage conservation: balancing access with preservation",
    ],
    openingStatements: [
      "France, a country 5 times smaller than India, attracts 9 times more international tourists. India's tourism deficit isn't about attractions — it's about infrastructure, safety, and marketing.",
      "India earned $30 billion from tourism in 2023. Thailand earned $60 billion with a fraction of India's diversity. The gap represents both a failure and a massive opportunity.",
    ],
    relatedSlugs: ["india-global-superpower", "digital-india-success-or-failure", "rural-vs-urban-development"],
  },
  {
    slug: "artificial-general-intelligence",
    title: "Should We Fear Artificial General Intelligence?",
    category: "Technology",
    overview: "While current AI is narrow (task-specific), Artificial General Intelligence (AGI) would match human cognitive abilities across all domains. Tech leaders like Elon Musk warn of existential risks, while others like Yann LeCun dismiss the fears as premature.",
    argumentsFor: [
      "AGI could solve humanity's greatest challenges — climate, disease, poverty",
      "We're decades away from AGI — current fears are speculative and premature",
      "AI safety research is actively developing alignment and control mechanisms",
      "Historical pattern: every transformative technology initially feared but ultimately beneficial",
      "Regulating AGI development can ensure safety while allowing progress",
    ],
    argumentsAgainst: [
      "Even narrow AI has caused harm — bias, job displacement, deepfakes",
      "AGI by definition would be beyond human control — no off switch",
      "Concentration of AGI power in few companies creates unprecedented risk",
      "AI alignment problem is unsolved — we can't guarantee AGI shares human values",
      "Elon Musk, Geoffrey Hinton, and other AI pioneers have warned of existential risk",
    ],
    discussionPoints: [
      "Timeline: when might AGI actually arrive — 10 years or 100?",
      "The AI alignment problem: can we make AGI share human values?",
      "Should there be an international body governing AGI development (like IAEA for nuclear)?",
      "Impact on employment if machines can do everything humans can",
      "Philosophical questions: consciousness, rights, and personhood of AGI",
    ],
    openingStatements: [
      "Geoffrey Hinton, the 'Godfather of AI,' quit Google specifically to warn about AI dangers. When the person who built the technology warns against it, we should at minimum listen carefully.",
      "We worry about AGI achieving superhuman intelligence, but we haven't even solved narrow AI's bias problem. Perhaps we should fix today's AI issues before panicking about tomorrow's.",
    ],
    relatedSlugs: ["artificial-intelligence-impact-on-jobs", "ethical-ai-development", "quantum-computing-future"],
  },
  {
    slug: "healthcare-access-india",
    title: "Healthcare Access in India: Crisis and Solutions",
    category: "Social Issues",
    overview: "India spends only 2.1% of GDP on public healthcare — among the lowest globally. With 1 doctor per 1,000 people (WHO recommends 1:250), healthcare access is a critical challenge. Ayushman Bharat aims to cover 500 million people but implementation gaps persist.",
    argumentsFor: [
      "Ayushman Bharat is the world's largest health insurance scheme covering 500M people",
      "India's pharmaceutical industry provides affordable medicines globally",
      "Telemedicine and digital health platforms are bridging the access gap",
      "Private healthcare sector provides world-class treatment (medical tourism)",
      "AIIMS and other public institutions offer free, high-quality specialized care",
    ],
    argumentsAgainst: [
      "India's public healthcare spending (2.1% of GDP) is far below WHO's 5% recommendation",
      "70% of healthcare spending in India is out-of-pocket — pushing millions into poverty",
      "Doctor-to-patient ratio is critically low, especially in rural areas",
      "Quality of care in primary health centers is poor — patients bypass them entirely",
      "Private healthcare costs are unaffordable for most Indians without insurance",
    ],
    discussionPoints: [
      "Should India implement universal healthcare like the UK's NHS?",
      "Role of technology (AI diagnostics, telemedicine) in rural healthcare",
      "Brain drain: why Indian doctors prefer to practice abroad",
      "Preventive healthcare vs. curative healthcare investment priorities",
      "Regulation of private hospital charges and medical negligence",
    ],
    openingStatements: [
      "55 million Indians are pushed into poverty every year due to healthcare costs. In a country with a space program and nuclear arsenal, this is not a resource problem — it's a priority problem.",
      "During COVID-19, India's healthcare system collapsed — patients died outside hospitals waiting for beds. The pandemic exposed what was always true: our healthcare infrastructure is critically inadequate.",
    ],
    relatedSlugs: ["rural-vs-urban-development", "population-boon-or-bane", "digital-india-success-or-failure"],
  },
];

import { expandedTopicsData } from "./gd-topics-expanded";

import { expandedTopicsData2 } from "./gd-topics-expanded-2";

// Merge base + expanded topics
const _mergedTopics = [...gdTopicsData, ...expandedTopicsData, ...expandedTopicsData2];
// Dedupe by slug (first occurrence wins) to keep React keys unique across pages.
const allTopics: GDTopicData[] = Array.from(
  new Map(_mergedTopics.map((t) => [t.slug, t])).values(),
);

export const getTopicBySlug = (slug: string): GDTopicData | undefined =>
  allTopics.find((t) => t.slug === slug);

export const getRelatedTopics = (slugs: string[]): GDTopicData[] =>
  slugs.map((s) => allTopics.find((t) => t.slug === s)).filter(Boolean) as GDTopicData[];

export const getTopicsByCategory = (): Record<string, GDTopicData[]> => {
  const map: Record<string, GDTopicData[]> = {};
  for (const topic of allTopics) {
    if (!map[topic.category]) map[topic.category] = [];
    map[topic.category].push(topic);
  }
  return map;
};

export const getAllTopics = (): GDTopicData[] => allTopics;
